// Button style B — soft controls. Segmented groups use Radix ToggleGroup
// (keyboard + ARIA) but render our own classes, so visuals stay identical.
import i18n from '@/i18n';
import * as RToggle from '@radix-ui/react-toggle-group';
import * as RSwitch from '@radix-ui/react-switch';
import type React from 'react';
import type { ReactNode } from 'react';
import { cx } from '@/lib/format';
import { Icon } from './Icon';

type Option<T extends string> = { value: T; label: ReactNode; className?: string; disabled?: boolean };

/** Single-choice segmented control; `className` = container class (`rp`, `sr-seg`, `bigseg`, …).
 *  `tabs` — the choice switches what the block shows (a view): a tablist with aria-selected and
 *  arrow keys (←/→, Home/End move and select). Otherwise a toggle group (filters, periods). */
export function Segmented<T extends string>({ value, onChange, options, className, label, id, tabs = false }: {
  value: T; onChange: (v: T) => void; options: Option<T>[]; className: string; label?: string; id?: string; tabs?: boolean;
}) {
  if (tabs) {
    const enabled = options.filter((o) => !o.disabled);
    const move = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const at = enabled.findIndex((o) => o.value === value);
      const to = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? at + 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? at - 1
        : e.key === 'Home' ? 0 : e.key === 'End' ? enabled.length - 1 : null;
      if (to === null) return;
      e.preventDefault();
      const next = enabled[(to + enabled.length) % enabled.length];
      onChange(next.value);
      (e.currentTarget.querySelector(`[data-value="${CSS.escape(next.value)}"]`) as HTMLElement | null)?.focus();
    };
    return (
      <div role="tablist" className={className} id={id} aria-label={label} onKeyDown={move}>
        {options.map((o) => (
          <button key={o.value} type="button" role="tab" data-value={o.value} aria-selected={o.value === value} tabIndex={o.value === value ? 0 : -1}
            disabled={o.disabled} className={cx(o.className, o.value === value && 'on')} onClick={() => onChange(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  return (
    <RToggle.Root type="single" className={className} id={id} value={value} aria-label={label}
      onValueChange={(v) => { if (v) onChange(v as T); }}>
      {options.map((o) => (
        <RToggle.Item key={o.value} value={o.value} disabled={o.disabled} className={cx(o.className, o.value === value && 'on')}>
          {o.label}
        </RToggle.Item>
      ))}
    </RToggle.Root>
  );
}

/** Pressed-in track with a raised knob (`.sw`), wrapped in a real switch. */
export function Switch({ checked, onChange, label, className = 'sw-btn' }: { checked: boolean; onChange: (v: boolean) => void; label: string; className?: string }) {
  return (
    <RSwitch.Root className={className} checked={checked} onCheckedChange={onChange} aria-label={label}>
      <span className="sw"><RSwitch.Thumb asChild><i /></RSwitch.Thumb></span>
    </RSwitch.Root>
  );
}

/** Round Prev/Next + page numbers (not used on a page right now, kept for the design kit). */
export function PageSwitcher({ pages, current, onChange, dark = false, prev = i18n.t('common:action.back'), next = i18n.t('nav:next') }: {
  pages: number; current: number; onChange: (p: number) => void; dark?: boolean; prev?: string; next?: string;
}) {
  return (
    <nav className={cx('pager', dark && 'dark')} aria-label={i18n.t('nav:pages')}>
      <button type="button" className="pg-arrow" disabled={current <= 1} onClick={() => onChange(current - 1)}><Icon name="chevl" /><span>{prev}</span></button>
      <div className="pg-nums">
        {Array.from({ length: pages }, (_, i) => (
          <button key={i} type="button" className={cx('pg-n', i + 1 === current && 'on')} onClick={() => onChange(i + 1)}>{i + 1}</button>
        ))}
      </div>
      <button type="button" className="pg-arrow" disabled={current >= pages} onClick={() => onChange(current + 1)}><Icon name="chev" /><span>{next}</span></button>
    </nav>
  );
}

/** Round soft icon button (`.rbtn`). */
export function RoundButton({ icon, label, onClick, big, dark, id, disabled }: { icon: string; label: string; onClick?: () => void; big?: boolean; dark?: boolean; id?: string; disabled?: boolean }) {
  return <button type="button" id={id} className={cx('rbtn', big && 'big', dark && 'dark')} aria-label={label} onClick={onClick} disabled={disabled}><Icon name={icon} /></button>;
}
